import axios from 'axios'

import router from '@/router/index'

const apiEndpoint = process.env.VUE_APP_API_ENDPOINT
const apiVersion = process.env.VUE_APP_API_VERSION

const apiClient = axios.create({
  baseURL: `${apiEndpoint}${apiVersion}`,
  withCredentials: false, // this is the default
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  timeout: 1000 * 60 * 5, // 5 minutes local (not cloud)
})

// declare a request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // perform a task before the request is sent
    const accessToken = localStorage.getItem('access_token')
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }

    return config
  },
  (error) => {
    // handle the error
    return Promise.reject(error)
  }
)

// declare a response interceptor
apiClient.interceptors.response.use(
  (response) => {
    return response
  },
  function (error) {
    const originalRequest = error.config

    // check refresh token
    if (
      error.response &&
      error.response.status === 401 &&
      originalRequest.url === '/auth/request-access-token'
    ) {
      router.push({ name: 'Login' }).catch(() => {})
      return Promise.reject(error)
    }

    // check access token
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true

      const refreshToken = localStorage.getItem('refresh_token')

      return apiClient
        .post('/auth/request-access-token', {
          refresh_token: refreshToken,
        })
        .then(({ data }) => {
          const { access_token, refresh_token } = data

          localStorage.setItem('access_token', access_token)
          localStorage.setItem('refresh_token', refresh_token)

          return apiClient(originalRequest)
        })
    }

    // check maintenance
    if (error.response && error.response.status === 503) {
      router.push({ name: 'Maintenance' }).catch(() => {})
      return Promise.reject(error)
    }

    // check network error
    if (error.message && error.message === 'Network Error') {
      axios
        .get('https://system.mastertime.io/system-status', {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        })
        .then((res) => {
          const result = res.data
          if (result[process.env.VUE_APP_MODE].system_status_code === 0) {
            router.push({ name: 'Maintenance' }).catch(() => {})
          } else {
            router
              .push({
                name: 'NetworkIssue',
                params: {
                  networkIssue: 'Internet Disconnected',
                },
              })
              .catch(() => {})
            return Promise.reject(error)
          }
        })
        .catch((error) => {
          router
            .push({
              name: 'NetworkIssue',
              params: {
                networkIssue: 'Internet Disconnected',
              },
            })
            .catch(() => {})
          return Promise.reject(error)
        })
    }

    // check network server connection 502
    if (error.response && error.response.status === 502) {
      axios
        .get('https://system.mastertime.io/system-status', {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        })
        .then((res) => {
          const result = res.data
          if (result[process.env.VUE_APP_MODE].system_status_code === 0) {
            router.push({ name: 'Maintenance' }).catch(() => {})
          } else {
            router
              .push({
                name: 'NetworkIssue',
                params: {
                  networkIssue: 'Server Connection Failed',
                },
              })
              .catch(() => {})
            return Promise.reject(error)
          }
        })
        .catch((error) => {
          router
            .push({
              name: 'NetworkIssue',
              params: {
                networkIssue: 'Server Connection Failed',
              },
            })
            .catch(() => {})
          return Promise.reject(error)
        })
    }

    // check network server connection 503
    if (error.response && error.response.status === 503) {
      axios
        .get('https://system.mastertime.io/system-status', {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        })
        .then((res) => {
          const result = res.data
          if (result[process.env.VUE_APP_MODE].system_status_code === 0) {
            router.push({ name: 'Maintenance' }).catch(() => {})
          } else {
            router
              .push({
                name: 'NetworkIssue',
                params: {
                  networkIssue: 'Server Connection Failed',
                },
              })
              .catch(() => {})
            return Promise.reject(error)
          }
        })
        .catch((error) => {
          router
            .push({
              name: 'NetworkIssue',
              params: {
                networkIssue: 'Server Connection Failed',
              },
            })
            .catch(() => {})
          return Promise.reject(error)
        })
    }

    // check network server connection timeout
    if (error.code && error.code === 'ECONNABORTED') {
      axios
        .get('https://system.mastertime.io/system-status', {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        })
        .then((res) => {
          const result = res.data
          if (result[process.env.VUE_APP_MODE].system_status_code === 0) {
            router.push({ name: 'Maintenance' }).catch(() => {})
          } else {
            router
              .push({
                name: 'NetworkIssue',
                params: {
                  networkIssue: 'Server Connection Timeout',
                },
              })
              .catch(() => {})
            return Promise.reject(error)
          }
        })
        .catch((error) => {
          router
            .push({
              name: 'NetworkIssue',
              params: {
                networkIssue: 'Server Connection Timeout',
              },
            })
            .catch(() => {})
          return Promise.reject(error)
        })
    }

    // handle reject error
    return Promise.reject(error)
  }
)

export default {
  // AUTHENTICATION
  software3rdParty(credential) {
    return apiClient.post('/auth/login/software3rdparty', credential)
  },
  login(credential) {
    return apiClient.post('/auth/login/taffadmin', credential)
  },
  changePassword(credential) {
    return apiClient.put(
      `/taffadmin/${credential.taff_admin_uuid}/password`,
      credential
    )
  },

  // CUSTOMER - COMPANY
  fetchCustomers() {
    return apiClient.get('/company')
  },
  fetchCustomer(uuid) {
    return apiClient.get(`/company/${uuid}`)
  },
  addCustomer(customer) {
    return apiClient.post('/company', customer)
  },
  editCustomer(customer) {
    return apiClient.put(`/company/${customer.company_uuid}`, customer)
  },
  fetchCustomerAdmins(uuid) {
    return apiClient.get(`/company/${uuid}/admin`)
  },
  fetchCustomerFeatures(uuid) {
    return apiClient.get(`/company-feature-toggle?company_uuid=${uuid}`)
  },
  addCustomerFeature(customerFeature) {
    return apiClient.post('/company-feature-toggle', customerFeature)
  },
  editCustomerFeature(customerFeature) {
    return apiClient.put(
      `/company-feature-toggle/${customerFeature.company_feature_toggle_uuid}`,
      customerFeature
    )
  },
  deleteCustomerFeature(uuid) {
    return apiClient.delete(`/company-feature-toggle/${uuid}`)
  },
  fetchCustomerUsageReportSummary({ uuid, startDate, endDate }) {
    return apiClient.get(
      `/company/${uuid}/usage-report/usage-summary?start_date=${startDate}&end_date=${endDate}`
    )
  },
  fetchCustomerWebhook(uuid) {
    return apiClient.get(`/webhook/company-reference/${uuid}`)
  },
  fetchCustomerWebhookSubscription({ company_uuid }) {
    return apiClient.get(`/company/${company_uuid}/webhook/subscription`)
  },
  configCustomerWebhook(payload) {
    return apiClient.post('/webhook/company-reference', payload.body)
  },
  // GENERATE FILE
  generateFile({ uuid, body }) {
    return apiClient.post(
      `/company/${uuid}/usage-report/usage-summary/generate-excel`,
      body
    )
  },
  generateFileProgress(job_process_uuid) {
    return apiClient.get(`/job-process/${job_process_uuid}`, {
      validateStatus: function (status) {
        return status < 400 // Resolve only if the status code is less than 400
      },
    })
  },

  // NOTIFICATION
  sendEmailToCompanyAboutToExpire(payload) {
    return apiClient.post(
      `/company/${payload.company_uuid}/expiration-notification/email`,
      payload.body
    )
  },

  // HARDWARE
  fetchHardwares(payload) {
    return apiClient.get('/hardware', { params: payload?.params })
  },
  fetchHardware(hardware_uuid) {
    return apiClient.get(`/hardware/${hardware_uuid}`)
  },
  fetchHardwareStatus() {
    return apiClient.get('/hardware-status')
  },
  fetchHardwareModels(payload) {
    return apiClient.get('/hardware-model', { params: payload?.params })
  },
  addHardware(hardware) {
    return apiClient.post('/hardware', hardware)
  },
  editHardware(hardware) {
    return apiClient.put(`/hardware/${hardware.hardware_uuid}`, hardware)
  },
  unRegisterHardware(hardware_uuid) {
    return apiClient.post(`/hardware/${hardware_uuid}/un-register`)
  },
  deleteHardware(hardware_uuid) {
    return apiClient.delete(`/hardware/${hardware_uuid}`)
  },
  hardwareReplicate({ hardware_uuid, data }) {
    return apiClient.post(`/hardware/${hardware_uuid}/replicate`, data)
  },

  // ADMIN
  fetchAdmins() {
    return apiClient.get('/taffadmin')
  },
  addAdmin(admin) {
    return apiClient.post('/taffadmin', admin)
  },
  editAdmin(admin) {
    return apiClient.put(`/taffadmin/${admin.taff_admin_uuid}`, admin)
  },
  deleteAdmin(uuid) {
    return apiClient.delete(`/taffadmin/${uuid}`)
  },

  // ANNOUNCEMENT
  fetchAnnouncements() {
    return apiClient.get('/system/announcement')
  },
  addAnnouncement(announcement) {
    return apiClient.post('/system/announcement', announcement)
  },
  editAnnouncement(announcement) {
    return apiClient.put(
      `/system/announcement/${announcement.system_announcement_uuid}`,
      announcement
    )
  },
  deleteAnnouncement(uuid) {
    return apiClient.delete(`/system/announcement/${uuid}`)
  },
  sendAnnouncement(payload) {
    return apiClient.post(
      `/system/announcement/${payload.system_announcement_uuid}/send`,
      payload.body
    )
  },

  // FEATURE
  fetchFeatures() {
    return apiClient.get('/feature')
  },

  // ROLE
  fetchRoles(roleType) {
    return apiClient.get(`/role?role_type=${roleType}`)
  },
  fetchCustomerAdminRoles(payload) {
    return apiClient.get(
      `/role?company_uuid=${payload.uuid}&role_type=${payload.roleType}`
    )
  },
  addCustomerAdmin(payload) {
    return apiClient.post(
      `/company/${payload.uuid}/admin`,
      payload.newCustomerAdmin
    )
  },
  editCustomerAdmin(payload) {
    return apiClient.put(
      `/company/${payload.uuid}/admin/${payload.customerAdminUpdate.company_admin_uuid}`,
      payload.customerAdminUpdate
    )
  },
  deleteCustomerAdmin(payload) {
    return apiClient.delete(
      `/company/${payload.uuid}/admin/${payload.company_admin_uuid}`
    )
  },

  // OTHER
  editCustomerConfigActivate(customer) {
    return apiClient.put(`/company/${customer.company_uuid}/config`, customer)
  },

  // FIRMWARE
  fetchFirmwares() {
    return apiClient.get('/system/firmware')
  },
  addFirmware({ firmware }) {
    return apiClient.post('/system/firmware', firmware)
  },
  editFirmware({ firmware_uuid, body }) {
    return apiClient.put(`/system/firmware/${firmware_uuid}`, body)
  },
  deleteFirmware({ firmware_uuid }) {
    return apiClient.delete(`/system/firmware/${firmware_uuid}`)
  },
}
